import {
  CardCatalogSchema,
  DeckDefinitionSchema,
  createEngineGameplayCatalog,
  expandDeckDefinition,
  validateDeck,
  type ArmyUpkeep,
  type CardCatalog,
  type DeckDefinition,
  type ResourceProduction,
} from "@fate-kingdom/card-data";

const CARD_DATA_VERSION = "0.3.0-test.1";

export const TEST_LAND_DEFINITION_IDS = {
  plains: "card.test-plains",
  plainsFrontier: "card.test-plains-frontier",
  forest: "card.test-forest",
  mountain: "card.test-mountain",
  river: "card.test-river",
} as const;

export const TEST_SUPPLY_ARMY_DEFINITION_IDS = {
  spearmen: "card.test-spearmen",
  cavalry: "card.test-cavalry",
  engineers: "card.test-engineers",
  siegeEngine: "card.test-siege-engine",
  merchants: "card.test-merchants",
  scholars: "card.test-scholars",
  diplomats: "card.test-diplomats",
} as const;

function makeLand(
  id: string,
  name: string,
  landTypeId: string,
  controls: readonly [number, number, number],
  production: readonly [ResourceProduction, ResourceProduction, ResourceProduction],
) {
  return {
    schemaVersion: 1,
    definitionVersion: 1,
    id,
    type: "land",
    name,
    cost: {},
    rulesText: "TEST ONLY: zero-cost Land used by deterministic engine scenarios.",
    landTypeId,
    developmentId: id,
    stages: [
      {
        stage: 1,
        title: `${name} Stage I`,
        maximumControl: controls[0],
        production: production[0],
        effects: [],
      },
      {
        stage: 2,
        title: `${name} Stage II`,
        maximumControl: controls[1],
        production: production[1],
        effects: [],
      },
      {
        stage: 3,
        title: `${name} Stage III`,
        maximumControl: controls[2],
        production: production[2],
        effects: [],
      },
    ],
  };
}

function makeSupplyArmy(
  id: string,
  name: string,
  upkeep: ArmyUpkeep,
  stats: { readonly armySize: number; readonly attack: number; readonly guard: number },
) {
  return {
    schemaVersion: 1,
    definitionVersion: 1,
    id,
    type: "army",
    name,
    cost: {},
    rulesText: "TEST ONLY: printed Army values used by deterministic Supply scenarios.",
    stats,
    upkeep,
    effects: [],
  };
}

function makeFillerArmy(index: number) {
  const suffix = String(index).padStart(2, "0");
  return {
    schemaVersion: 1,
    definitionVersion: 1,
    id: `card.test-filler-army-${suffix}`,
    type: "army",
    name: `TEST ONLY Filler Army ${suffix}`,
    cost: {},
    rulesText: "TEST ONLY: inert deck filler; no effect is executed in this slice.",
    stats: {
      armySize: 1,
      attack: 0,
      guard: 0,
    },
    upkeep: {},
    effects: [],
  };
}

export const TEST_CARD_CATALOG: CardCatalog = CardCatalogSchema.parse({
  schemaVersion: 1,
  cardDataVersion: CARD_DATA_VERSION,
  cards: [
    makeLand(
      TEST_LAND_DEFINITION_IDS.plains,
      "TEST ONLY Plains",
      "plains",
      [10, 14, 18],
      [{ food: 1 }, { food: 2, production: 1 }, { food: 3, production: 2 }],
    ),
    makeLand(
      TEST_LAND_DEFINITION_IDS.plainsFrontier,
      "TEST ONLY Plains Frontier",
      "plains",
      [11, 15, 19],
      [
        { food: 1, trade: 1 },
        { food: 2, trade: 1 },
        { food: 3, trade: 2 },
      ],
    ),
    makeLand(
      TEST_LAND_DEFINITION_IDS.forest,
      "TEST ONLY Forest",
      "forest",
      [9, 13, 17],
      [
        { food: 1, culture: 1 },
        { food: 2, culture: 1 },
        { food: 2, culture: 2 },
      ],
    ),
    makeLand(
      TEST_LAND_DEFINITION_IDS.mountain,
      "TEST ONLY Mountain",
      "mountain",
      [9, 13, 17],
      [{ production: 2 }, { production: 3 }, { production: 4 }],
    ),
    makeLand(
      TEST_LAND_DEFINITION_IDS.river,
      "TEST ONLY River",
      "river",
      [8, 12, 16],
      [
        { food: 1, trade: 1 },
        { food: 2, trade: 1 },
        { food: 2, trade: 2 },
      ],
    ),
    makeSupplyArmy(
      TEST_SUPPLY_ARMY_DEFINITION_IDS.spearmen,
      "TEST ONLY Spearmen",
      { food: 1 },
      { armySize: 1, attack: 1, guard: 1 },
    ),
    makeSupplyArmy(
      TEST_SUPPLY_ARMY_DEFINITION_IDS.cavalry,
      "TEST ONLY Cavalry",
      { food: 1, production: 1 },
      { armySize: 2, attack: 3, guard: 1 },
    ),
    makeSupplyArmy(
      TEST_SUPPLY_ARMY_DEFINITION_IDS.engineers,
      "TEST ONLY Engineers",
      { production: 1 },
      { armySize: 1, attack: 1, guard: 2 },
    ),
    makeSupplyArmy(
      TEST_SUPPLY_ARMY_DEFINITION_IDS.siegeEngine,
      "TEST ONLY Siege Engine",
      { production: 3 },
      { armySize: 3, attack: 4, guard: 0 },
    ),
    makeSupplyArmy(
      TEST_SUPPLY_ARMY_DEFINITION_IDS.merchants,
      "TEST ONLY Merchants",
      { trade: 1 },
      { armySize: 1, attack: 0, guard: 1 },
    ),
    makeSupplyArmy(
      TEST_SUPPLY_ARMY_DEFINITION_IDS.scholars,
      "TEST ONLY Scholars",
      { science: 1 },
      { armySize: 1, attack: 0, guard: 1 },
    ),
    makeSupplyArmy(
      TEST_SUPPLY_ARMY_DEFINITION_IDS.diplomats,
      "TEST ONLY Diplomats",
      { culture: 1 },
      { armySize: 1, attack: 0, guard: 1 },
    ),
    ...Array.from({ length: 14 }, (_, index) => makeFillerArmy(index + 1)),
  ],
  effects: [],
  conditions: [],
  triggers: [],
});

export const TEST_ENGINE_CATALOG = createEngineGameplayCatalog(TEST_CARD_CATALOG);

function deckInput(
  id: string,
  name: string,
  cards: readonly { readonly cardId: string; readonly count: number }[],
) {
  return {
    schemaVersion: 1,
    definitionVersion: 1,
    cardDataVersion: CARD_DATA_VERSION,
    id,
    name: `TEST ONLY: ${name}`,
    cards,
  };
}

function fillerEntries(
  first: number,
  last: number,
  count: number,
): readonly { readonly cardId: string; readonly count: number }[] {
  return Array.from({ length: last - first + 1 }, (_, offset) => ({
    cardId: `card.test-filler-army-${String(first + offset).padStart(2, "0")}`,
    count,
  }));
}

function parseLegalDeck(input: unknown): DeckDefinition {
  const structurallyValid = DeckDefinitionSchema.parse(input);
  const result = validateDeck(structurallyValid, TEST_CARD_CATALOG);
  if (!result.success) {
    throw new Error(`Invalid test-only legal deck: ${result.error.message}`);
  }
  return result.data;
}

const player1 = parseLegalDeck(
  deckInput("deck.test-player-1", "Player 1 setup deck", [
    { cardId: TEST_LAND_DEFINITION_IDS.plains, count: 3 },
    { cardId: TEST_LAND_DEFINITION_IDS.mountain, count: 3 },
    { cardId: TEST_LAND_DEFINITION_IDS.river, count: 3 },
    ...fillerEntries(1, 10, 3),
    { cardId: "card.test-filler-army-11", count: 1 },
  ]),
);

const player2 = parseLegalDeck(
  deckInput("deck.test-player-2", "Player 2 setup deck", [
    { cardId: TEST_LAND_DEFINITION_IDS.river, count: 3 },
    { cardId: TEST_LAND_DEFINITION_IDS.plains, count: 3 },
    { cardId: TEST_LAND_DEFINITION_IDS.mountain, count: 3 },
    { cardId: "card.test-filler-army-04", count: 1 },
    ...fillerEntries(5, 14, 3),
  ]),
);

const oneLand = parseLegalDeck(
  deckInput("deck.test-one-land", "single-Land redraw deck", [
    { cardId: TEST_LAND_DEFINITION_IDS.plains, count: 1 },
    ...fillerEntries(1, 13, 3),
  ]),
);

const zeroLand = DeckDefinitionSchema.parse(
  deckInput("deck.test-zero-land", "invalid zero-Land deck", [
    ...fillerEntries(1, 13, 3),
    { cardId: "card.test-filler-army-14", count: 1 },
  ]),
);

const stageThree = parseLegalDeck(
  deckInput("deck.test-stage-three", "Stage III Land deck", [
    { cardId: TEST_LAND_DEFINITION_IDS.plains, count: 3 },
    { cardId: TEST_LAND_DEFINITION_IDS.mountain, count: 1 },
    { cardId: TEST_LAND_DEFINITION_IDS.river, count: 1 },
    ...fillerEntries(1, 11, 3),
    { cardId: "card.test-filler-army-12", count: 2 },
  ]),
);

const recycle = parseLegalDeck(
  deckInput("deck.test-recycle", "draw and recycle deck", [
    { cardId: TEST_LAND_DEFINITION_IDS.mountain, count: 3 },
    { cardId: TEST_LAND_DEFINITION_IDS.river, count: 3 },
    { cardId: TEST_LAND_DEFINITION_IDS.plains, count: 3 },
    ...fillerEntries(1, 10, 3),
    { cardId: "card.test-filler-army-14", count: 1 },
  ]),
);

const supply = parseLegalDeck(
  deckInput("deck.test-supply", "Land type and Supply scenario deck", [
    ...Object.values(TEST_LAND_DEFINITION_IDS).map((cardId) => ({ cardId, count: 3 })),
    ...Object.values(TEST_SUPPLY_ARMY_DEFINITION_IDS).map((cardId) => ({ cardId, count: 3 })),
    ...fillerEntries(1, 4, 1),
  ]),
);

export const TEST_DECKS = {
  player1,
  player2,
  oneLand,
  zeroLand,
  stageThree,
  recycle,
  supply,
} as const;

export const TEST_DECK_CARD_IDS = {
  player1: expandDeckDefinition(TEST_DECKS.player1),
  player2: expandDeckDefinition(TEST_DECKS.player2),
  oneLand: expandDeckDefinition(TEST_DECKS.oneLand),
  zeroLand: expandDeckDefinition(TEST_DECKS.zeroLand),
  stageThree: expandDeckDefinition(TEST_DECKS.stageThree),
  recycle: expandDeckDefinition(TEST_DECKS.recycle),
  supply: expandDeckDefinition(TEST_DECKS.supply),
} as const;
